import React, { PureComponent } from "react";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import MomentUtils from "@date-io/moment";
import { MuiPickersUtilsProvider } from "material-ui-pickers";
import { Redirect, Route, Switch } from "react-router-dom";
import { connect } from "react-redux";
import { IntlProvider } from "react-intl";
import "assets/vendors/style";
import defaultTheme from "./themes/defaultTheme";
import AppLocale from "../lngProvider";
import { initializeInterceptors } from "../util/interceptors";
import { AUTH_CONSTANTS } from "../lib/dev-auth-manager";

import MainApp from "app/index";
import {
  setInitUrl,
  loadTranslations,
  userSignInSuccess,
  updateAuthToken,
} from "../actions/Auth";
import RTL from "util/RTL";
import asyncComponent from "util/asyncComponent";

// import StorageHandler from "../util/storageHandler";
import { setUser, saveSearchParam } from "../actions";
import { getTranslationsList } from "../actions/adminSettings/translations/strings/actionsTranslationStrings";
import DownTimeBanner from "components/DownTimeBanner";
import StorageHandler from "util/storageHandler";
import ProtectedRoute from "components/protected-route";
import { validateUser } from "sagas/Auth";
import { store } from "MainApp";
import { checkIfInMaintenance } from "util/maintenance";
import {
  enableMaintenanceBanner,
  initialMaintenanceCheckCompleted,
} from "actions/adminSettings/maintenanceBanner/ActionsMaintenanceBanner";
import { HTTP_REQUEST_STATUS_CODES } from "constants/constants";
import {
  hasAdminAccessOrPermissions,
  isSuperAdmin,
} from "features/auth/helpers";
import { permissions } from "features/auth/permissions";
import { routes } from "util/routes";
import { IntlContextProvider } from "hooks/use-translation";

export const validateAndLoadUser = async () => {
  const response = await validateUser();
  if (response.status == 200 && response.data.user) {
    let user = response.data.user;

    const isUserHasTemplateCreateAccess = hasAdminAccessOrPermissions(user, [
      permissions.canCreateQuestionnaires,
    ]);
    const accessToken = response.data.access;

    let userState = {
      user: user,
      authUser: response.data.user,
      bearerHeader: { Authorization: `Bearer ${accessToken}` },
      refreshKey: response.data.refresh,
      accessToken: accessToken,
    };

    if (
      typeof user["preferred_client"] != "undefined" &&
      StorageHandler.getItem(StorageHandler.keys.PREFERRED_CLIENT) !==
        user["preferred_client"]
    ) {
      StorageHandler.saveItem(
        StorageHandler.keys.PREFERRED_CLIENT,
        user["preferred_client"]
      );
    }

    StorageHandler.saveCurrentUserName(user["user_name"]);
    StorageHandler.saveUserState(user["user_name"], userState);

    const userData = {
      ...userState,
      isUserHasTemplateCreateAccess: isUserHasTemplateCreateAccess,
      isSuperAdmin: isSuperAdmin(user),
      access: accessToken,
    };

    store.dispatch(setUser(userData));
    store.dispatch(userSignInSuccess(userData));

    return { userData, responseStatus: response.status };
  }
  if (
    response.response.status === HTTP_REQUEST_STATUS_CODES.UNAUTHORIZED &&
    response.response?.data?.code
  ) {
    return {
      userData: null,
      code: response.response.data.code,
      responseStatus: response.response.status,
    };
  }
  return { userData: null, responseStatus: response.response.status };
};

// an initial maintenance check that runs once when the module loads
const maintenancePromise = checkIfInMaintenance().catch(() => false);

function isOnSurveyResponsePage(location) {
  return location.pathname.startsWith("/survey_response");
}

class App extends PureComponent {
  async componentDidMount() {
    // Initialize all interceptors
    initializeInterceptors();
    if (!isOnSurveyResponsePage(this.props.location)) {
      store.dispatch(getTranslationsList());
    }
    const isInMaintenance = await maintenancePromise;
    store.dispatch(enableMaintenanceBanner(isInMaintenance));
    store.dispatch(initialMaintenanceCheckCompleted());
    if (isInMaintenance) {
      window.location.href = `${process.env.REACT_APP_NEXT_APP_URL}/next/maintenance`;
    }

    // Add storage event listener to handle access token changes
    window.addEventListener("storage", this.handleStorageChange);
  }

  componentWillMount() {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    if (this.props.initURL === "") {
      this.props.setInitUrl(this.props.history.location.pathname);
    }

    if (typeof this.props.history.location.search !== "undefined") {
      this.props.saveSearchParam(this.props.history.location.search);
    }
  }

  componentWillUnmount() {
    if (this.authEventListener) {
      this.authEventListener();
    }
    // Remove storage event listener
    window.removeEventListener("storage", this.handleStorageChange);
  }

  handleStorageChange = (event) => {
    if (
      event.key ===
      `oidc.user:${AUTH_CONSTANTS.SETTINGS.AUTHORITY}:${AUTH_CONSTANTS.SETTINGS.CLIENT_ID}`
    ) {
      if (!event.newValue) {
        store.dispatch(updateAuthToken(""));
      }
    }
  };

  componentDidUpdate(prevProps) {
    const { remoteTranslations } = this.props;
    const hasRemoteTranslations = Object.keys(remoteTranslations).length > 0;
    const prevHasTranslations =
      Object.keys(prevProps.remoteTranslations).length > 0;

    if (hasRemoteTranslations && !prevHasTranslations) {
      this.props.loadTranslations();
    }
  }

  render() {
    const {
      match,
      location,
      locale,
      initURL,
      isDirectionRTL,
      remoteTranslations,
      isInitialTranslationsLoadComplete,
    } = this.props;

    if (location.pathname === "/") {
      if (initURL === "" || initURL === "/") {
        return <Redirect to={"/app/dashboard"} />;
      } else {
        return <Redirect to={initURL} />;
      }
    } else if (isOnSurveyResponsePage(location)) {
      window.location.href = routes.engage(location.search);
      return;
    }

    const applyTheme = createTheme(defaultTheme);

    if (isDirectionRTL) {
      applyTheme.direction = "rtl";
      document.body.classList.add("rtl");
    } else {
      document.body.classList.remove("rtl");
      applyTheme.direction = "ltr";
    }

    const currentAppLocale = AppLocale[locale.locale];

    const localMessages = AppLocale["en-US"]?.messages || {};
    const currentAppLocaleMessages = currentAppLocale?.messages || {};
    const remoteMessages = remoteTranslations[locale.locale]?.data || {};

    // Merge remote translations with default messages
    const messages = {
      ...localMessages,
      ...currentAppLocaleMessages,
      ...remoteMessages,
    };

    return (
      <MuiThemeProvider theme={applyTheme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <IntlProvider locale={currentAppLocale.locale} messages={messages}>
            <IntlContextProvider>
              <RTL>
                <DownTimeBanner>
                  <Switch>
                    <ProtectedRoute
                      path={`${match.url}app`}
                      component={MainApp}
                      remoteTranslations={remoteTranslations}
                      isInitialTranslationsLoadComplete={
                        isInitialTranslationsLoadComplete ||
                        isOnSurveyResponsePage(location)
                      }
                    />
                    <Route
                      component={asyncComponent(() =>
                        import("components/Error404")
                      )}
                    />
                  </Switch>
                </DownTimeBanner>
              </RTL>
            </IntlContextProvider>
          </IntlProvider>
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    );
  }
}

const mapStateToProps = ({ settings, auth }) => {
  const {
    locale,
    isDirectionRTL,
    remoteTranslations,
    isInitialTranslationsLoadComplete,
  } = settings;
  const { initURL, user } = auth;
  return {
    locale,
    isDirectionRTL,
    initURL,
    user,
    remoteTranslations,
    isInitialTranslationsLoadComplete,
  };
};

const ConnectedApp = connect(mapStateToProps, {
  setInitUrl,
  setUser,
  getTranslationsList,
  saveSearchParam,
  userSignInSuccess,
  loadTranslations,
  updateAuthToken,
})(App);

export default ConnectedApp;
